import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Home, Loader2, FolderOpen, Search, Download, ExternalLink, CalendarDays, Maximize2 } from "lucide-react";
import { SiteLogo } from "@/components/SiteLogo";
import { BrandText } from "@/components/BrandText";
import api, { mediaUrl } from "@/lib/api";
import { downloadUrl, formatSize } from "@/lib/uploads";
import { DocViewerDialog, useDocPreview } from "@/components/PdfViewer";
import { attachmentIcon } from "@/components/AttachmentCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "");

export function DocumentRow({ doc, actions }) {
  const Icon = attachmentIcon(doc.filename || doc.title);
  const href = mediaUrl(doc.url);
  const { previewable: isPdf, pdfUrl } = useDocPreview(href, doc.filename || doc.title || "");
  const [viewer, setViewer] = useState(false);
  return (
    <div data-testid={`document-row-${doc.id}`} className="group flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-white/10 bg-[#0F172A] px-4 py-3 hover:border-gold/40 transition-colors">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-11 h-11 shrink-0 rounded-lg bg-gold/10 border border-gold/30 flex items-center justify-center text-gold"><Icon className="w-5 h-5" /></div>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate" data-testid="document-title">{doc.title}</p>
          <p className="text-[11px] text-muted-foreground flex flex-wrap items-center gap-x-2">
            <span className="uppercase tracking-wider text-gold/90">{doc.category_label}</span>
            {doc.discipline && <span>· {doc.discipline === "ritmica" ? "Ritmica" : doc.discipline === "artistica" ? "Artistica" : doc.discipline}</span>}
            {doc.event_title && <span className="inline-flex items-center gap-1">· <CalendarDays className="w-3 h-3" /> {doc.event_title}</span>}
            {doc.size > 0 && <span>· {formatSize(doc.size)}</span>}
            {doc.created_at && <span>· {fmtDate(doc.created_at)}</span>}
          </p>
          {doc.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{doc.description}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {isPdf
          ? <button type="button" data-testid={`document-open-${doc.id}`} onClick={() => setViewer(true)} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-white/15 hover:border-gold/60 hover:text-gold transition-colors"><Maximize2 className="w-3.5 h-3.5" /> Sfoglia</button>
          : <a data-testid={`document-open-${doc.id}`} href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-white/15 hover:border-gold/60 hover:text-gold transition-colors"><ExternalLink className="w-3.5 h-3.5" /> Apri</a>}
        {isPdf && <DocViewerDialog href={href} name={doc.filename || doc.title} pdfUrl={pdfUrl} open={viewer} onClose={() => setViewer(false)} />}
        <a data-testid={`document-download-${doc.id}`} href={downloadUrl(href)} download className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-gold text-[#020617] hover:bg-[#FDE047] transition-colors"><Download className="w-3.5 h-3.5" /> Scarica</a>
        {actions}
      </div>
    </div>
  );
}

export function useDocuments() {
  const [data, setData] = useState({ documents: [], categories: {}, disciplines: {} });
  const [loading, setLoading] = useState(true);
  const reload = () => api.get("/documents").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { reload(); }, []);
  return { ...data, loading, reload, setData };
}

export function DocumentFilters({ docs, categories, filters, setFilters }) {
  const events = useMemo(() => {
    const m = new Map();
    docs.forEach((d) => { if (d.event_id && d.event_title) m.set(d.event_id, d.event_title); });
    return Array.from(m.entries());
  }, [docs]);
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-3">
      <div className="relative w-full lg:max-w-xs">
        <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <Input data-testid="documents-search" value={filters.q} onChange={(e) => set("q")(e.target.value)} placeholder="Cerca un documento…" className="pl-9 bg-[#0B1220] border-white/15" />
      </div>
      <div className="flex flex-wrap gap-2">
        {[["tutte", "Tutte"], ...Object.entries(categories)].map(([k, label]) => (
          <button key={k} data-testid={`documents-cat-${k}`} onClick={() => set("category")(k)}
            className={`px-3.5 py-2 rounded-full text-xs sm:text-sm font-medium border transition-colors ${filters.category === k ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-muted-foreground hover:border-gold/40"}`}>{label}</button>
        ))}
      </div>
      <Select value={filters.discipline} onValueChange={set("discipline")}>
        <SelectTrigger data-testid="documents-discipline" className="w-full lg:w-[170px] bg-[#0B1220] border-white/15 h-10"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="tutte">Tutte le discipline</SelectItem><SelectItem value="artistica">Artistica</SelectItem><SelectItem value="ritmica">Ritmica</SelectItem></SelectContent>
      </Select>
      <Select value={filters.event} onValueChange={set("event")}>
        <SelectTrigger data-testid="documents-event" className="w-full lg:w-[220px] bg-[#0B1220] border-white/15 h-10"><SelectValue placeholder="Tutte le gare" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="tutte">Tutte le gare</SelectItem>
          {events.map(([id, title]) => <SelectItem key={id} value={id}>{title}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export function filterDocuments(docs, f) {
  const term = f.q.trim().toLowerCase();
  return docs.filter((d) =>
    (f.category === "tutte" || d.category === f.category) &&
    (f.discipline === "tutte" || (d.discipline || "") === f.discipline) &&
    (f.event === "tutte" || d.event_id === f.event) &&
    (!term || `${d.title} ${d.description} ${d.event_title} ${d.filename}`.toLowerCase().includes(term)));
}

export const EMPTY_FILTERS = { q: "", category: "tutte", discipline: "tutte", event: "tutte" };

export default function Documents() {
  const { documents, categories, loading } = useDocuments();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const filtered = useMemo(() => filterDocuments(documents, filters), [documents, filters]);

  return (
    <div className="min-h-screen bg-background text-foreground noise-bg overflow-x-hidden">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 min-w-0">
            <SiteLogo className="w-9 h-9 shrink-0" />
            <div className="flex flex-col leading-tight min-w-0 truncate"><BrandText /></div>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-gold transition-colors inline-flex items-center gap-1.5 shrink-0"><Home className="w-4 h-4" /> Home</Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-gold border border-gold/40 rounded-full px-3 py-1"><FolderOpen className="w-3 h-3" /> Documenti</span>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4">Area Documenti</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">Regolamenti, moduli, circolari e verbali ufficiali delle gare. Filtra per categoria, disciplina o gara e scarica ciò che ti serve.</p>

        <div className="mt-8"><DocumentFilters docs={documents} categories={categories} filters={filters} setFilters={setFilters} /></div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground border border-dashed border-white/10 rounded-xl mt-8" data-testid="documents-empty">
            {documents.length === 0 ? "Nessun documento pubblicato." : "Nessun documento per i filtri selezionati."}
          </div>
        ) : (
          <div className="space-y-3 mt-8" data-testid="documents-list">
            {filtered.map((d) => <DocumentRow key={d.id} doc={d} />)}
          </div>
        )}
      </main>
    </div>
  );
}
